import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { sanitizeElement } from '../../../../../utils/common';

type LogRecord = Record<string, unknown>;

/** Truncate a timestamp to the minute for grouping (ignore seconds/ms differences). */
const toMinuteKey = (raw: unknown): string => {
  if (!raw || typeof raw !== 'string') return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`;
  } catch {
    return String(raw);
  }
};

const logGroupKey = (log: LogRecord): string => {
  const time = toMinuteKey(log.time ?? log.createdAt ?? log.dateTime);
  const user = String(log.userName ?? log.user ?? log.createdBy ?? '');
  return `${time}_${user}`;
};

/**
 * Group interaction-event logs with their triggering log (same minute + user).
 * Returns logs with `_attachedInteractions` for combined rendering.
 */
export const groupInteractionsWithTrigger = (logs: LogRecord[]): LogRecord[] => {
  const interactionMap = new Map<string, LogRecord[]>();
  const nonInteractionLogs: LogRecord[] = [];
  for (const log of logs) {
    const code = String(log.logTypeCode ?? '').toLowerCase();
    if (code === 'interaction-events') {
      const key = logGroupKey(log);
      if (!interactionMap.has(key)) interactionMap.set(key, []);
      interactionMap.get(key)!.push(log);
    } else {
      nonInteractionLogs.push(log);
    }
  }
  const result = nonInteractionLogs.map((log) => {
    const key = logGroupKey(log);
    const attached = interactionMap.get(key);
    if (attached) {
      interactionMap.delete(key);
      return { ...log, _attachedInteractions: attached };
    }
    return log;
  });
  for (const orphans of interactionMap.values()) {
    result.push(...orphans);
  }
  return result;
};

/** Safely render notes that may contain HTML, using DOMPurify. */
export const SafeNotes: React.FC<{ html: string }> = ({ html }) => {
  if (!html || html === '-') {
    return <>{html || '-'}</>;
  }

  // Only render as HTML if it actually contains tags
  if (/<[a-z][\s\S]*>/i.test(html)) {
    const sanitized = sanitizeElement(html);
    return (
      <Box
        component="span"
        sx={{ '& p': { m: 0 }, '& br': { display: 'block', content: '""', mt: 0.25 }, fontSize: 'inherit' }}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  return <>{html}</>;
};

/** Render interaction form field values inline beneath the note. */
export const InteractionDetails: React.FC<{ data: unknown }> = ({ data }) => {
  if (!data || typeof data !== 'object') return null;
  const forms = (data as Record<string, unknown>).interactionForms as Record<string, unknown> | undefined;
  if (!forms) return null;
  const title = String(forms.title ?? '');
  const containers = (forms.containers ?? []) as Array<{ fields?: Array<{ title?: string; value?: unknown; type?: string }> }>;

  const fields = containers.flatMap((c) => c.fields ?? []).filter((f) => f.value != null && f.type !== 'Label');
  if (fields.length === 0 && !title) return null;

  return (
    <Box sx={{ mt: 0.5, pl: 1, borderLeft: '2px solid', borderColor: 'v4.border', fontSize: '11px', color: 'text.secondary' }}>
      {title && (
        <Typography sx={{ fontSize: '11px', fontWeight: 600, mb: 0.25 }}>{title}</Typography>
      )}
      {fields.map((f, i) => (
        <Box key={i}>
          <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>{f.title}: </Typography>
          <Typography component="span" sx={{ fontSize: '11px' }}>{typeof f.value === 'object' ? JSON.stringify(f.value) : String(f.value)}</Typography>
        </Box>
      ))}
    </Box>
  );
};
